import { useAiReceipts } from '../hooks/useAiReceipts.js'
import { AGENT_LABELS } from '../lib/agents.js'
import { timeAgo } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

// AiReceiptsCard — "AI Receipts — this week" (Ship 9).
//
// A deterministic recap of what each ARM agent did in the past 7 days.
// Renders ≤8 rows, each: the agent's label (from AGENT_LABELS, never an
// inline string), a one-sentence concrete outcome, an optional dollar chip
// (only for dollar-impact rows), a humanized timestamp (timeAgo), and an
// optional in-app click-through.
//
// Mounted on the single-unit Daily Huddle (scope={{ restaurantId }}) and the
// group All-Units dashboard (scope={{ groupId }}). The data hook picks the
// right edge fn. NO Anthropic, NO cost/model language anywhere on this
// surface (customer-facing house rule).
//
// Props:
//   scope    — { restaurantId } | { groupId }
//   onTab    — (tab: string) => void  click-through handler (huddle: onSwitchTab,
//              group: setView). Optional; rows are non-interactive without it.
//
// React #310: useAiReceipts is called BEFORE any conditional return.

const LOADER_MESSAGES = [
  'Gathering this week’s receipts…',
  'Tallying what your agents did…',
  'Lining up the highlights…',
]

// Map a receipt entry's link tab onto the host screen's tab/view value. The
// single-unit huddle uses tab strings directly; the group screen uses view
// strings (catering → 'catering-breakdown'). Unmapped tabs are non-clickable.
function resolveTab(linkTab, isGroup) {
  if (!linkTab) return null
  if (!isGroup) return linkTab // huddle tabs map 1:1
  switch (linkTab) {
    case 'catering':
      return 'catering-breakdown'
    case 'reorder':
      return 'reorder'
    case 'comparison':
      return 'comparison'
    default:
      return null // marketing/schedule/huddle have no group overlay
  }
}

export default function AiReceiptsCard({ scope, onTab, className = 'mx-4 mt-4' }) {
  const isGroup = !!scope?.groupId
  const { phase, entries } = useAiReceipts(scope)

  // Eyebrow uses sentence case, no emoji, no cost/model language.
  const eyebrow = 'AI receipts — this week'

  return (
    <section
      className={`${className} rounded-2xl border border-black/10 bg-white p-5 shadow-sm`}
      aria-label={eyebrow}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-black/45">
        {eyebrow}
      </div>

      {phase === 'loading' && (
        <div className="mt-3">
          <KitchenLoader align="row" size="sm" tone="light" messages={LOADER_MESSAGES} />
        </div>
      )}

      {(phase === 'empty' || phase === 'error') && (
        <p className="mt-3 text-[13px] leading-relaxed text-black/60">
          Your AI agents will start tracking their work here as soon as they&rsquo;ve done a
          few things. Generate a Huddle brief, draft a Marketing post, or quote a Catering
          inquiry to begin.
        </p>
      )}

      {phase === 'ready' && entries.length > 0 && (
        <ul className="mt-3 space-y-2">
          {entries.map((entry, i) => (
            <ReceiptRow
              key={`${entry.agent_key}-${entry.timestamp}-${i}`}
              entry={entry}
              isGroup={isGroup}
              onTab={onTab}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function ReceiptRow({ entry, isGroup, onTab }) {
  // Never render undefined: fall back to a neutral label if the key is missing.
  const label = AGENT_LABELS[entry.agent_key] || 'AI agent'
  const when = timeAgo(entry.timestamp)
  const tab = resolveTab(entry?.link?.tab, isGroup)
  const clickable = !!(tab && onTab)
  const showChip = entry.impact_type === 'dollars' && entry.impact_display

  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-medium text-black/45">
            {label}
            {entry.unit_name ? ` · ${entry.unit_name}` : ''}
          </div>
          <div className="mt-0.5 text-[13px] leading-snug text-black/85">
            {entry.headline_text}
          </div>
        </div>
        {showChip && (
          <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700">
            {entry.impact_display}
          </span>
        )}
      </div>
      {when && <div className="mt-1 text-[11px] text-black/40">{when}</div>}
    </>
  )

  if (clickable) {
    return (
      <li>
        <button
          type="button"
          onClick={() => onTab(tab)}
          className="block w-full rounded-xl border border-black/[0.06] bg-black/[0.015] px-3 py-2.5 text-left transition hover:bg-black/[0.04] focus:outline-none focus:ring-2 focus:ring-black/10"
        >
          {inner}
        </button>
      </li>
    )
  }

  return (
    <li className="rounded-xl border border-black/[0.06] bg-black/[0.015] px-3 py-2.5">
      {inner}
    </li>
  )
}
